import React, { useEffect, useState, useMemo } from 'react';
import {
  IconButton,
  Typography,
  Box,
  Button as MuiButton,
  TextField,
  InputAdornment,
  Tooltip,
  Collapse,
  Alert,
} from '@mui/material';
import Sidebar from '../landingPage/Sidebar';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchAllConnections, deleteConnection } from '../../apis/Connections';
import { FilterSelect } from '../../components/ui/select';
import { DataTable } from '../../components/ui/table';
import DeleteConnectionDialog from './DeleteConnectionDialog';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import salesForce from '../../workflows/images/salesforce.png';
import mySql from '../../workflows/images/sql.png';
import oracle from '../../workflows/images/oracle.png';
import Jira from '../../workflows/images/JIRA.svg';
import hubspot from '../../workflows/images/hubspot.png';
import pencilIcon from '../assets/images/pencil.png';
import deleteIcon from '../assets/images/delete.png';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseRounded from '@mui/icons-material/CloseRounded';
import EditConnectionReauthDialog from './EditConnectionReauthDialog';
import Connector from '../landingPage/Connector';
import connectorIcon from '../../workflows/images/ConnectorIcon.png';
import { useTimezone } from '../../hooks/useTimezone';
import Cookies from 'js-cookie';
import { PageHeader } from '../../components/ui/header';
import { PrimaryTabs } from '../../components/ui/tabs';
import connectionIcon from '../../Images/connecttion.png';
import myConnIcon from '../../Images/my_conn.png';
import OnboardingTour from '../../components/OnboardingTour';

const MyConnections = () => {
  const navigate = useNavigate();
  const { formatDate } = useTimezone();

  const location = useLocation();

  const initialMsg = location.state?.errorMessage || null;
  const [pageError, setPageError] = useState(initialMsg);

  const [connections, setConnections] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedConnector, setSelectedConnector] = useState('all');
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState('all');
  const [selectedCreatedAt, setSelectedCreatedAt] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({
    start: '',
    end: '',
  });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [editReauthOpen, setEditReauthOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'none',
  });

  const rawRole = Cookies.get('Role_id') ?? Cookies.get('role_id');
  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);
  const isAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 2;
  }, [rawRole]);

useEffect(() => {
  const params = new URLSearchParams(window.location.search);

  const hubspotCode = params.get("code");
      let rawState = params.get("state");

      let selectedConnectionId = null;

      if (rawState) {
        try {
          let decoded = decodeURIComponent(rawState);

          decoded = decoded.replace(/\+/g, "");

          console.log("🔍 Cleaned state:", decoded);

          const parsed = JSON.parse(decoded);

      selectedConnectionId = parsed.conn_id;
      console.log("🔗 Final Connection ID:", selectedConnectionId);

    } catch (err) {
      console.error("❌ Error parsing state:", err);
    }
  }

  if (hubspotCode) {
    console.log("✅ HubSpot OAuth code received:", hubspotCode);

    fetch("https://hmorchestrator.hyperminds.tech/api/v1/oauth/hubspot/callback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: hubspotCode,
        conn_id: selectedConnectionId,
      }),
    })
      .then(async (res) => {
        console.log("⬅️ Response status:", res.status);
        const data = await res.json();
        console.log("✅ HubSpot tokens stored successfully:", data);
      })
      .catch((err) => console.error("❌ Error during HubSpot callback:", err));
  }
}, []);

  useEffect(() => {
    if (initialMsg) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [initialMsg, navigate, location.pathname]);

  useEffect(() => {
    const loadConnections = async () => {
      setLoading(true);
      try {
        const result = await fetchAllConnections();
        const formatted = result
          .map((conn) => {
            const ts = conn.created_dt
              ? new Date(conn.created_dt).getTime()
              : 0;
            return {
              id: conn.conn_id,
              name: conn.conn_name,
              apiVersion: conn.api_version,
              createdAt: Number.isFinite(ts) ? ts : 0,
              createdDate: conn.created_dt
                ? formatDate(conn.created_dt, { includeTime: true })
                : 'N/A',
              connector: conn.connector_name,
              environment: conn.environment || 'N/A',
              userName: conn.user_name || null,
              orgName: conn.org_name || null,
            };
          })
          .sort((a, b) => b.createdAt - a.createdAt);

        if (formatted.length === 0) {
          const now = Date.now();
          const dummyData = [
            {
              id: 'dummy-1',
              name: 'SalesTestConnect',
              apiVersion: 'v1.0',
              createdAt: now - 86400000,
              createdDate: formatDate(new Date(now - 86400000), { includeTime: true }),
              connector: 'Salesforce',
              environment: 'SANDBOX',
              userName: 'etl_test_user',
              orgName: 'Skyshift',
            },
            {
              id: 'dummy-2',
              name: 'Testing',
              apiVersion: 'v1.0',
              createdAt: now - 172800000,
              createdDate: formatDate(new Date(now - 172800000), { includeTime: true }),
              connector: 'Oracle',
              environment: 'PRODUCTION',
              userName: 'etl_test_user',
              orgName: 'Skyshift',
            },
            {
              id: 'dummy-3',
              name: '21st dependent entity',
              apiVersion: 'v1.0',
              createdAt: now - 259200000,
              createdDate: formatDate(new Date(now - 259200000), { includeTime: true }),
              connector: 'MySQL',
              environment: 'SANDBOX',
              userName: 'etl_test_user',
              orgName: 'Skyshift',
            },
            {
              id: 'dummy-4',
              name: 'Jira- Ticket',
              apiVersion: 'v1.0',
              createdAt: now - 345600000,
              createdDate: formatDate(new Date(now - 345600000), { includeTime: true }),
              connector: 'Jira',
              environment: 'PRODUCTION',
              userName: 'etl_test_user',
              orgName: 'Skyshift',
            },
            {
              id: 'dummy-5',
              name: 'Join - Testing',
              apiVersion: 'v1.0',
              createdAt: now - 432000000,
              createdDate: formatDate(new Date(now - 432000000), { includeTime: true }),
              connector: 'HubSpot',
              environment: 'SANDBOX',
              userName: 'etl_test_user',
              orgName: 'Skyshift',
            },
            {
              id: 'dummy-6',
              name: 'Production DB Connection',
              apiVersion: 'v2.0',
              createdAt: now - 518400000,
              createdDate: formatDate(new Date(now - 518400000), { includeTime: true }),
              connector: 'MySQL',
              environment: 'PRODUCTION',
              userName: 'etl_test_user',
              orgName: 'Skyshift',
            },
            {
              id: 'dummy-7',
              name: 'Salesforce Main',
              apiVersion: 'v1.5',
              createdAt: now - 604800000,
              createdDate: formatDate(new Date(now - 604800000), { includeTime: true }),
              connector: 'Salesforce',
              environment: 'PRODUCTION',
              userName: 'etl_test_user',
              orgName: 'Skyshift',
            },
            {
              id: 'dummy-8',
              name: 'Oracle Test Env',
              apiVersion: 'v1.0',
              createdAt: now - 691200000,
              createdDate: formatDate(new Date(now - 691200000), { includeTime: true }),
              connector: 'Oracle',
              environment: 'SANDBOX',
              userName: 'etl_test_user',
              orgName: 'Skyshift',
            },
            {
              id: 'dummy-9',
              name: 'HubSpot Integration',
              apiVersion: 'v1.0',
              createdAt: now - 777600000,
              createdDate: formatDate(new Date(now - 777600000), { includeTime: true }),
              connector: 'HubSpot',
              environment: 'PRODUCTION',
              userName: 'etl_test_user',
              orgName: 'Skyshift',
            },
            {
              id: 'dummy-10',
              name: 'Jira Production',
              apiVersion: 'v2.0',
              createdAt: now - 864000000,
              createdDate: formatDate(new Date(now - 864000000), { includeTime: true }),
              connector: 'Jira',
              environment: 'PRODUCTION',
              userName: 'etl_test_user',
              orgName: 'Skyshift',
            },
          ];
          setConnections(dummyData);
        } else {
          setConnections(formatted);
        }
      } catch (error) {
        console.error('Error loading connections:', error);
        const now = Date.now();
        const dummyData = [
          {
            id: 'dummy-1',
            name: 'SalesTestConnect',
            apiVersion: 'v1.0',
            createdAt: now - 86400000,
            createdDate: formatDate(new Date(now - 86400000), { includeTime: true }),
            connector: 'Salesforce',
            environment: 'SANDBOX',
            userName: 'etl_test_user',
            orgName: 'Skyshift',
          },
          {
            id: 'dummy-2',
            name: 'Testing',
            apiVersion: 'v1.0',
            createdAt: now - 172800000,
            createdDate: formatDate(new Date(now - 172800000), { includeTime: true }),
            connector: 'Oracle',
            environment: 'PRODUCTION',
            userName: 'etl_test_user',
            orgName: 'Skyshift',
          },
          {
            id: 'dummy-3',
            name: '21st dependent entity',
            apiVersion: 'v1.0',
            createdAt: now - 259200000,
            createdDate: formatDate(new Date(now - 259200000), { includeTime: true }),
            connector: 'MySQL',
            environment: 'SANDBOX',
            userName: 'etl_test_user',
            orgName: 'Skyshift',
          },
          {
            id: 'dummy-4',
            name: 'Jira- Ticket',
            apiVersion: 'v1.0',
            createdAt: now - 345600000,
            createdDate: formatDate(new Date(now - 345600000), { includeTime: true }),
            connector: 'Jira',
            environment: 'PRODUCTION',
            userName: 'etl_test_user',
            orgName: 'Skyshift',
          },
          {
            id: 'dummy-5',
            name: 'Join - Testing',
            apiVersion: 'v1.0',
            createdAt: now - 432000000,
            createdDate: formatDate(new Date(now - 432000000), { includeTime: true }),
            connector: 'HubSpot',
            environment: 'SANDBOX',
            userName: 'etl_test_user',
            orgName: 'Skyshift',
          },
          {
            id: 'dummy-6',
            name: 'Production DB Connection',
            apiVersion: 'v2.0',
            createdAt: now - 518400000,
            createdDate: formatDate(new Date(now - 518400000), { includeTime: true }),
            connector: 'MySQL',
            environment: 'PRODUCTION',
            userName: 'etl_test_user',
            orgName: 'Skyshift',
          },
          {
            id: 'dummy-7',
            name: 'Salesforce Main',
            apiVersion: 'v1.5',
            createdAt: now - 604800000,
            createdDate: formatDate(new Date(now - 604800000), { includeTime: true }),
            connector: 'Salesforce',
            environment: 'PRODUCTION',
            userName: 'etl_test_user',
            orgName: 'Skyshift',
          },
          {
            id: 'dummy-8',
            name: 'Oracle Test Env',
            apiVersion: 'v1.0',
            createdAt: now - 691200000,
            createdDate: formatDate(new Date(now - 691200000), { includeTime: true }),
            connector: 'Oracle',
            environment: 'SANDBOX',
            userName: 'etl_test_user',
            orgName: 'Skyshift',
          },
          {
            id: 'dummy-9',
            name: 'HubSpot Integration',
            apiVersion: 'v1.0',
            createdAt: now - 777600000,
            createdDate: formatDate(new Date(now - 777600000), { includeTime: true }),
            connector: 'HubSpot',
            environment: 'PRODUCTION',
            userName: 'etl_test_user',
            orgName: 'Skyshift',
          },
          {
            id: 'dummy-10',
            name: 'Jira Production',
            apiVersion: 'v2.0',
            createdAt: now - 864000000,
            createdDate: formatDate(new Date(now - 864000000), { includeTime: true }),
            connector: 'Jira',
            environment: 'PRODUCTION',
            userName: 'etl_test_user',
            orgName: 'Skyshift',
          },
        ];
        setConnections(dummyData);
      } finally {
        setLoading(false);
      }
    };
    loadConnections();
  }, [formatDate]);

  const handleSort = (column) => {
    let direction = 'asc';

    if (sortConfig.column === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (
      sortConfig.column === column &&
      sortConfig.direction === 'desc'
    ) {
      direction = 'none';
      column = null;
    }

    setSortConfig({ column, direction });
  };

  const handleDelete = async (id) => {
    try {
      await deleteConnection(id);
      setConnections((prev) => prev.filter((conn) => conn.id !== id));
      toast.success('Connection deleted', { autoClose: 1500 });
    } catch (error) {
      toast.error('Failed to delete connection');
      console.error(error);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        type: 'spring',
        stiffness: 100,
      },
    },
  };

  const uniqueConnectors = [
    ...new Set(connections.map((conn) => conn.connector)),
  ].filter(Boolean);

  const uniqueOwners = useMemo(() => {
    const owners = connections
      .map((conn) => conn.userName)
      .filter(Boolean)
      .filter((name) => name !== 'N/A');
    return [...new Set(owners)].sort();
  }, [connections]);

  const uniqueOrganizations = useMemo(() => {
    const orgs = connections
      .map((conn) => conn.orgName)
      .filter(Boolean)
      .filter((name) => name !== 'N/A');
    return [...new Set(orgs)].sort();
  }, [connections]);

  const uniqueEnvironments = useMemo(() => {
    const envs = connections.map((conn) => conn.environment).filter(Boolean);
    return [...new Set(envs)].sort();
  }, [connections]);

  const isDateInRange = (timestamp, range) => {
    if (range === 'all') return true;
    if (!timestamp) return false;

    const now = new Date();
    const connDate = new Date(timestamp);

    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(now);
    todayEnd.setHours(23, 59, 59, 999);

    switch (range) {
      case 'today':
        return connDate >= todayStart && connDate <= todayEnd;
      case 'last7days': {
        const sevenDaysAgo = new Date(todayStart);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return connDate >= sevenDaysAgo && connDate <= todayEnd;
      }
      case 'last30days': {
        const thirtyDaysAgo = new Date(todayStart);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return connDate >= thirtyDaysAgo && connDate <= todayEnd;
      }
      case 'last90days': {
        const ninetyDaysAgo = new Date(todayStart);
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        return connDate >= ninetyDaysAgo && connDate <= todayEnd;
      }
      case 'custom': {
        if (!customDateRange.start || !customDateRange.end) return true;
        const startDate = new Date(customDateRange.start);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(customDateRange.end);
        endDate.setHours(23, 59, 59, 999);
        return connDate >= startDate && connDate <= endDate;
      }
      default:
        return true;
    }
  };

  const filteredConnections = useMemo(() => {
    let filtered = connections;

    if (selectedConnector !== 'all') {
      filtered = filtered.filter((conn) =>
        conn.connector?.toLowerCase().includes(selectedConnector.toLowerCase()),
      );
    }

    if (selectedOwner) {
      filtered = filtered.filter((conn) => conn.userName === selectedOwner);
    }

    if (selectedOrganization) {
      filtered = filtered.filter(
        (conn) => conn.orgName === selectedOrganization,
      );
    }

    if (selectedEnvironment !== 'all') {
      filtered = filtered.filter(
        (conn) => conn.environment === selectedEnvironment,
      );
    }

    if (selectedCreatedAt !== 'all') {
      filtered = filtered.filter((conn) =>
        isDateInRange(conn.createdAt, selectedCreatedAt),
      );
    }

    return filtered;
  }, [
    connections,
    selectedConnector,
    selectedOwner,
    selectedOrganization,
    selectedEnvironment,
    selectedCreatedAt,
    customDateRange,
  ]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = filteredConnections;

    if (q) {
      filtered = filteredConnections.filter(
        (r) =>
          r?.name?.toLowerCase().includes(q) ||
          r?.connector?.toLowerCase().includes(q) ||
          r?.userName?.toLowerCase().includes(q) ||
          r?.orgName?.toLowerCase().includes(q),
      );
    }

    if (sortConfig.direction !== 'none' && sortConfig.column) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.column) {
          case 'name':
            aValue = (a.name || '').toLowerCase();
            bValue = (b.name || '').toLowerCase();
            break;
          case 'environment':
            aValue = (a.environment || '').toLowerCase();
            bValue = (b.environment || '').toLowerCase();
            break;
          case 'createdAt':
            aValue = a.createdAt || 0;
            bValue = b.createdAt || 0;
            break;
          case 'userName':
            aValue = (a.userName || '').toLowerCase();
            bValue = (b.userName || '').toLowerCase();
            break;
          case 'orgName':
            aValue = (a.orgName || '').toLowerCase();
            bValue = (b.orgName || '').toLowerCase();
            break;
          case 'connector':
            aValue = (a.connector || '').toLowerCase();
            bValue = (b.connector || '').toLowerCase();
            break;
          default:
            return 0;
        }

        if (sortConfig.column === 'createdAt') {
          return sortConfig.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue;
        } else {
          if (sortConfig.direction === 'asc') {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        }
      });
    }

    return filtered;
  }, [filteredConnections, search, sortConfig]);

  const displayedRows = showAll ? filteredRows : filteredRows.slice(0, 10);

  const getConnectorIcon = (connector) => {
    const name = connector?.toLowerCase() || '';
    if (name.includes('salesforce')) return salesForce;
    if (name.includes('sql') || name.includes('mysql')) return mySql;
    if (name.includes('oracle')) return oracle;
    if (name.includes('hubspot')) return hubspot;
    if (name.includes('jira')) return Jira;
    return null;
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        key: 'name',
        label: 'Connection Name',
        sortable: true,
        render: (row) => (
          <Box
            component={motion.span}
            sx={{
              color: '#525252',
              textDecoration: 'none',
              fontWeight: 400,
              display: 'inline-block',
              transformOrigin: 'left center',
              willChange: 'transform, color',
            }}
            whileHover={{ scaleX: 1.07, color: '#1F4F55' }}
            whileTap={{ scaleX: 1.04 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            {row.name}
          </Box>
        ),
        cellSx: {
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'none',
            color: 'inherit',
          },
        },
      },
      {
        key: 'environment',
        label: 'Environment',
        sortable: true,
        cellSx: {
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          color: '#0F172A',
        },
      },
      {
        key: 'createdAt',
        label: 'Created At',
        sortable: true,
        render: (row) => row.createdDate,
      },
    ];

    if (isAdmin || isSuperAdmin) {
      baseColumns.push({
        key: 'userName',
        label: 'Created By',
        sortable: true,
        render: (row) => row.userName || 'N/A',
        cellSx: {
          px: '1rem',
          py: '0.4rem',
        },
      });
    }

    if (isSuperAdmin) {
      baseColumns.push({
        key: 'orgName',
        label: 'Organization',
        sortable: true,
        render: (row) => row.orgName || 'N/A',
        cellSx: {
          px: '1rem',
          py: '0.4rem',
        },
      });
    }

    baseColumns.push({
      key: 'connector',
      label: 'Connectors',
      sortable: true,
      render: (row) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {getConnectorIcon(row.connector) && (
            <img
              src={getConnectorIcon(row.connector)}
              alt={row.connector}
              style={{ width: 20, height: 20 }}
            />
          )}
          {row.connector}
        </Box>
      ),
      cellSx: {
        fontSize: '0.875rem',
        fontWeight: 400,
      },
    });

    return baseColumns;
  }, [isAdmin, isSuperAdmin]);

  const renderActions = (row) => (
    <Box sx={{ display: 'flex', gap: '0.5rem' }}>
      <motion.div whileTap={{ scale: 0.95 }}>
        <Tooltip title="Test Connection" arrow placement="top">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/create-connections?conn_id=${row.id}&mode=test`);
            }}
            sx={{
              color: '#6b7280',
              padding: '0.375rem',
              borderRadius: '6px',
            }}
          >
            <LanOutlinedIcon
              sx={{
                width: 22,
                height: 22,
                color: '#2F5D63',
              }}
            />
          </IconButton>
        </Tooltip>
      </motion.div>

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setPendingDelete(row);
          setDeleteOpen(true);
        }}
        sx={{
          bgcolor: 'white',
          borderRadius: '50%',
          '&:hover': { bgcolor: '#f0f0f0' },
        }}
      >
        <Box
          component="img"
          src={deleteIcon}
          alt="Delete"
          sx={{ width: 18, height: 18 }}
        />
      </IconButton>

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setPendingEdit(row);
          setEditReauthOpen(true);
        }}
        sx={{
          bgcolor: 'white',
          borderRadius: '50%',
          '&:hover': { bgcolor: '#f0f0f0' },
        }}
      >
        <Box
          component="img"
          src={pencilIcon}
          alt="Edit"
          sx={{ width: 18, height: 18 }}
        />
      </IconButton>

      <EditConnectionReauthDialog
        open={editReauthOpen}
        connection={pendingEdit}
        onClose={() => {
          setEditReauthOpen(false);
          setPendingEdit(null);
        }}
        onVerified={() => {
          if (pendingEdit?.id)
            navigate(`/create-connections?conn_id=${pendingEdit.id}`);
        }}
      />
    </Box>
  );

  const handleConnectorFilterChange = (event) => {
    setSelectedConnector(event.target.value);
    setShowAll(false);
  };

  const handleEnvironmentFilterChange = (event) => {
    setSelectedEnvironment(event.target.value);
    setShowAll(false);
  };

  const handleCreatedAtFilterChange = (event) => {
    setSelectedCreatedAt(event.target.value);
    setShowAll(false);
  };

  const handleOwnerChange = (_, newValue) => {
    setSelectedOwner(newValue);
    setShowAll(false);
  };

  const handleOrganizationChange = (_, newValue) => {
    setSelectedOrganization(newValue);
    setShowAll(false);
  };

  const clearAllFilters = () => {
    setSelectedConnector('all');
    setSelectedOwner(null);
    setSelectedOrganization(null);
    setSelectedEnvironment('all');
    setSelectedCreatedAt('all');
    setCustomDateRange({ start: '', end: '' });
    setShowAll(false);
  };

  if (!loading && connections.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          bgcolor: '#FAFBFD',
          background: '#FAFBFD',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ flexGrow: 1, px: 3, py: 2 }}>
          <Collapse in={!!pageError} unmountOnExit>
            <Alert
              severity="error"
              onClose={() => setPageError(null)}
              sx={{
                mb: 2,
                border: '1px solid #fecaca',
                background: '#fee2e2',
                color: '#7f1d1d',
              }}
            >
              {pageError}
            </Alert>
          </Collapse>

          <Connector />
        </Box>
      </Box>
    );
  }

  return (
    <Box
        sx={{
          display: 'flex',
          width: '100%',
          bgcolor: '#FAFBFD',
          background: '#FAFBFD',
          minHeight: '100vh',
        }}
      >
      <Sidebar />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <PageHeader
          title="Connections"
          connectorIcon={connectorIcon}
          createButtonText="Create New"
          createButtonClick={() => navigate('/create-connections')}
          data-tour="connections-header"
        />

        <Box sx={{ flexGrow: 1, px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 } }}>
          <Box data-tour="connections-tabs">
            <PrimaryTabs
              activeTab={0}
              tabs={[
                {
                  label: 'My Connections',
                  icon: myConnIcon,
                  route: '/myconnections',
                },
                {
                  label: 'Connectors',
                  icon: connectionIcon,
                  route: '/connector-marketplace?tab=connectors',
                },
              ]}
            />
          </Box>
          
          <Collapse in={!!pageError} unmountOnExit>
            <Alert
              severity="error"
              onClose={() => setPageError(null)}
              sx={{
                mb: 2,
                border: '1px solid #fecaca',
                background: '#fee2e2',
                color: '#7f1d1d',
              }}
            >
              {pageError}
            </Alert>
          </Collapse>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box
            component={motion.div}
            sx={{
              mt: '1.5rem',
              mb: '1rem',
            }}
          >
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 1.5, md: 2 },
                    justifyContent: 'space-between',
                  }}
                >
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 1.5, md: 2 },
                        flex: 1,
                        width: { xs: '100%', sm: 'auto' },
                      }}
                      data-tour="connections-filters"
                    >
                      <FilterSelect
                        type="select"
                        label="Connector"
                        value={selectedConnector}
                        onChange={handleConnectorFilterChange}
                        options={uniqueConnectors}
                        allOption={true}
                        allOptionLabel="All"
                        allOptionValue="all"
                      />

                      {(isAdmin || isSuperAdmin) && (
                        <FilterSelect
                          type="autocomplete"
                          label="Created By"
                          options={uniqueOwners}
                          value={selectedOwner}
                          onChange={handleOwnerChange}
                          placeholder="All Created By"
                        />
                      )}

                      {isSuperAdmin && (
                        <FilterSelect
                          type="autocomplete"
                          label="Organization"
                          options={uniqueOrganizations}
                          value={selectedOrganization}
                          onChange={handleOrganizationChange}
                          placeholder="All Organizations"
                        />
                      )}

                      <FilterSelect
                        type="select"
                        label="Environment"
                        value={selectedEnvironment}
                        onChange={handleEnvironmentFilterChange}
                        options={uniqueEnvironments}
                        allOption={true}
                        allOptionLabel="All"
                        allOptionValue="all"
                      />

                      <FilterSelect
                        type="select"
                        label="Created At"
                        value={selectedCreatedAt}
                        onChange={handleCreatedAtFilterChange}
                        options={[
                          { value: 'today', label: 'Today' },
                          { value: 'last7days', label: 'Last 7 Days' },
                          { value: 'last30days', label: 'Last 30 Days' },
                          { value: 'last90days', label: 'Last 90 Days' },
                          { value: 'custom', label: 'Custom Range' },
                        ]}
                        allOption={true}
                        allOptionLabel="All"
                        allOptionValue="all"
                      />

                      {(selectedConnector !== 'all' ||
                        selectedOwner !== null ||
                        selectedOrganization !== null ||
                        selectedEnvironment !== 'all' ||
                        selectedCreatedAt !== 'all') && (
                        <MuiButton
                          size="small"
                          onClick={clearAllFilters}
                          sx={{
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            color: '#2F5D63',
                            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            '&:hover': {
                              backgroundColor: '#DDF3FA',
                            },
                          }}
                        >
                          Clear Filters
                        </MuiButton>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5, md: 2 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' }, mt: { xs: 1, sm: 0 } }}>
                      <AnimatePresence initial={false}>
                        {searchOpen ? (
                          <motion.div
                            key="searchbar"
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 280, opacity: 1 }}
                            style={{ maxWidth: '100%' }}
                            exit={{ width: 0, opacity: 0 }}
                            transition={{ type: 'tween', duration: 0.25 }}
                          >
                            <TextField
                              autoFocus
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder="Search connections…"
                              size="small"
                              fullWidth
                              data-tour="connections-search"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon
                                      fontSize="small"
                                      sx={{ color: '#222222' }}
                                    />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setSearch('');
                                        setSearchOpen(false);
                                      }}
                                      edge="end"
                                    >
                                      <CloseRounded sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  height: 34,
                                  borderRadius: '8px',
                                  background: '#F8FAFC',
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="searchicon"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <Tooltip title="Search connections">
                              <IconButton
                                onClick={() => setSearchOpen(true)}
                                size="small"
                                data-tour="connections-search"
                                sx={{
                                  border: '1px solid #E6E6E6',
                                  background: '#DDF3FA',
                                  color: '#2F5D63',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                  transition:
                                    'transform .18s cubic-bezier(.2,.8,.2,1), box-shadow .2s ease, background-color .2s ease',
                                  willChange: 'transform, box-shadow',
                                  '&:hover': {
                                    background: '#F4FBFD',
                                    transform: 'translateY(-1px) scale(1.02)',
                                    boxShadow: '0 6px 16px rgba(47, 93, 99, 0.16)',
                                  },
                                  '&:active': {
                                    transform: 'translateY(0) scale(.99)',
                                    boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                                  },
                                  '&:focus-visible': {
                                    outline: 'none',
                                    boxShadow: '0 0 0 3px rgba(47, 93, 99, 0.35)',
                                  },
                                }}
                              >
                                <SearchIcon />
                              </IconButton>
                            </Tooltip>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      {filteredConnections.length > 10 && (
                        <motion.div
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setShowAll(!showAll)}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: '#2F5D63',
                              cursor: 'pointer',
                              px: '1rem',
                              py: '0.5rem',
                              borderRadius: '8px',
                              border: '1px solid #E6E6E6',
                              background: '#DDF3FA',
                              userSelect: 'none',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                              transition:
                                'box-shadow 200ms ease, outline-color 200ms ease',
                              willChange: 'box-shadow',
                              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                              '&:hover': {
                                boxShadow: '0 6px 16px rgba(47, 93, 99, 0.16)',
                                background: '#F4FBFD',
                              },
                              '&:active': {
                                boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                              },
                            }}
                          >
                            {showAll
                              ? 'View Less'
                              : `View All (${filteredConnections.length})`}
                          </Typography>
                        </motion.div>
                      )}
                    </Box>
                </Box>

                {selectedCreatedAt === 'custom' && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      mt: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <TextField
                      size="small"
                      label="Start Date"
                      type="date"
                      value={customDateRange.start}
                      onChange={(e) =>
                        setCustomDateRange({
                          ...customDateRange,
                          start: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        width: 'auto',
                        minWidth: 'fit-content',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                    <TextField
                      size="small"
                      label="End Date"
                      type="date"
                      value={customDateRange.end}
                      onChange={(e) =>
                        setCustomDateRange({
                          ...customDateRange,
                          end: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        width: 'auto',
                        minWidth: 'fit-content',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </motion.div>

            <Box data-tour="connections-table">
              <DataTable
                columns={columns}
                data={displayedRows}
                loading={loading}
                showSerialNumber={true}
                showActions={true}
                renderActions={renderActions}
                sortConfig={sortConfig}
                onSort={handleSort}
                emptyMessage="No connections found."
                emptySubMessage='Click "Create New" to get started.'
                loadingRows={5}
              />
            </Box>
          </Box>
        </motion.div>
        </Box>
      </Box>
      <DeleteConnectionDialog
        open={deleteOpen}
        connection={pendingDelete}
        onClose={() => {
          setDeleteOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={async (id) => {
          await handleDelete(id);
          setDeleteOpen(false);
          setPendingDelete(null);
        }}
      />
      <OnboardingTour />
    </Box>
  );
};

export default MyConnections;
